import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import useBitrateAutopilot from "../lib/useBitrateAutopilot";
import { Mic, MicOff, Video, VideoOff, Radio, Square, Users, MessageSquare, Send, CircleDot, SwitchCamera, Maximize, Cast, Sparkles } from "lucide-react";
import ViewerStatsHud from "./ViewerStatsHud";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fetchIceServers, makeWsUrl, newPeerConnection } from "../lib/webrtc";
import { useMediaStream } from "../lib/useMediaStream";
import { useCameraCompositor, cameraSettingsToCss } from "../lib/useCameraCompositor";
import StreamWatermark from "./StreamWatermark";
import { useEmoteBursts } from "./Emotes";
import CrewBoostButton from "./CrewBoostButton";
import CameraPermissionGate from "./CameraPermissionGate";
import { applyAnnouncerFx } from "../lib/announcerFx";
import { applyAutoFix } from "../lib/audioAutoFix";
import AudioLevelMeter from "./AudioLevelMeter";
import AudioQualityMeter from "./AudioQualityMeter";
import GreenScreenStudio from "./GreenScreenStudio";

/**
 * WebRTC broadcaster — captures camera + mic, opens a WS to /api/ws/stream/{id},
 * and answers viewer_ready with a targeted SDP offer per viewer_id. Each viewer
 * gets its own RTCPeerConnection. Retries the offer every 5s until the viewer
 * acks by entering the `connected` ICE state.
 */
const LiveBroadcaster = forwardRef(function LiveBroadcaster(
  { streamId, kidsBrand = false, hideChat = false, hideCrewBoost = false, hideInlineControls = false, cameraSettings = null, onEnd, onStatusChange },
  ref
) {
  const { user, token } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | requesting | live | error | ended
  const [errMsg, setErrMsg] = useState("");
  const [viewers, setViewers] = useState([]);
  // Going-Live countdown — 3-second overlay shown right when the stream
  // transitions to "live" so the host + first viewers see a pro "3-2-1
  // GO" reveal. Watches `status` to fire once per transition. Zero
  // backend cost, pure UX polish.
  const [liveCountdown, setLiveCountdown] = useState(0);
  useEffect(() => {
    if (status !== "live") return undefined;
    setLiveCountdown(3);
    const t1 = setTimeout(() => setLiveCountdown(2), 1000);
    const t2 = setTimeout(() => setLiveCountdown(1), 2000);
    const t3 = setTimeout(() => setLiveCountdown(0), 3000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [status]);

  // Auto-tweet on LIVE — fires exactly once per session when the
  // creator hits AIR. Opt-in via the SocialLinksCard toggle in /studio;
  // requires the creator to have saved a Twitter handle (used as
  // hashtag context) or we still open a tweet with just the URL.
  // Uses the public twitter.com/intent/tweet endpoint — zero server
  // auth needed. Popup blockers may interfere; we swallow silently.
  const autoTweetFiredRef = useRef(false);
  useEffect(() => {
    if (status !== "live" || autoTweetFiredRef.current) return;
    try {
      const enabled = localStorage.getItem("tv_auto_tweet_on_live") === "1";
      if (!enabled) return;
      autoTweetFiredRef.current = true;
      const url = `${window.location.origin}/live/${streamId}`;
      const handle = user?.social_links?.twitter || "";
      const mention = handle
        ? ` — follow ${handle.replace(/https?:\/\/(www\.)?(x\.com|twitter\.com)\//, "@")}`
        : "";
      const text = `🔴 I'm LIVE on Tap IN TV Network right now${mention} — come join!`;
      const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      window.open(twUrl, "_blank", "noopener,noreferrer,width=550,height=420");
    } catch { /* popup blocked or storage disabled */ }
  }, [status, streamId, user]);
  // Per-viewer ICE connection state, keyed by viewer peer_id. Used by the
  // host-only "Live viewer stats" HUD so the creator can see at a glance
  // which viewers are healthy, dropping frames, or reconnecting.
  const [viewerStates, setViewerStates] = useState({});
  const [chat, setChat] = useState([]);
  const [chatMsg, setChatMsg] = useState("");
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);

  const previewContainerRef = useRef(null);
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const pcsRef = useRef({});
  const iceServersRef = useRef([{ urls: ["stun:stun.l.google.com:19302"] }]);
  const guestPcsRef = useRef({}); // guestId -> { pc, stream }
  // Tracks the most recent video track sent to peers via replaceVideoTrack
  // so highlight clips taken before any peer connects still have video.
  const lastOutboundVideoTrackRef = useRef(null);
  const chatEndRef = useRef(null);
  const { add: addEmote, Overlay: EmoteOverlay } = useEmoteBursts();

  // Adaptive bitrate autopilot — watches every active peer's RTP stats
  // and downsteps maxBitrate when the link gets congested (loss > 5%
  // or RTT > 400 ms), upsteps back when 3 ticks come back clean.
  // Drops zero frames during the transition — the existing video
  // sender just re-encodes at the new ceiling.
  useBitrateAutopilot({
    enabled: status === "live",
    getPcs: () => pcsRef.current,
  });

  // --- WebRTC resilience (keepalive + auto-reconnect + ICE restart) ---
  const keepAliveRef = useRef(null);       // interval id for ws ping frames
  const heartbeatRef = useRef(null);       // interval id for host-presence heartbeat
  const reconnectTimerRef = useRef(null);  // setTimeout id for next reconnect attempt
  const reconnectAttemptsRef = useRef(0);
  const wantCloseRef = useRef(false);      // true when we intentionally closed (don't reconnect)
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const media = useMediaStream();

  // --- Recording state (MediaRecorder of local preview stream) ---
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | saved | failed
  const [replayVideoId, setReplayVideoId] = useState(null);

  // --- Green-room preflight removed (iter151) — startBroadcast now goes
  // straight to signaling; users used to have to click Go Live → I'm Ready.
  // Kept refs out so future agents don't re-add the friction by accident.

  // --- Announcer FX (broadcast-grade voice processing) ---
  const announcerFxRef = useRef(null); // { stream, dispose } | null
  const [announcerFxOn, setAnnouncerFxOn] = useState(false);

  // --- Auto-Fix (real-time clip-limit + noise gate + tracking notch) ---
  // Sits *upstream* of Announcer FX so cleaning happens before tone shaping.
  const autoFixRef = useRef(null); // { stream, dispose, getStatus } | null
  const [autoFixOn, setAutoFixOn] = useState(false);

  // Returns the audio track currently flowing into WebRTC senders, taking
  // both Auto-Fix and Announcer FX into account. Used by the new-viewer
  // pcSetup to make sure late joiners get the processed feed too.
  const getEffectiveAudioTrack = () => {
    if (announcerFxRef.current) return announcerFxRef.current.stream.getAudioTracks?.()[0];
    if (autoFixRef.current)     return autoFixRef.current.stream.getAudioTracks?.()[0];
    return streamRef.current?.getAudioTracks?.()[0];
  };

  // Rebuild the entire audio processing chain to match the requested
  // {autoFix, announcer} state. Called by both toggle handlers so the
  // Announcer-FX → Auto-Fix interaction is one-place-only and we never
  // end up with two competing chains feeding the same senders.
  //
  // Topology:  raw → [AutoFix] → [Announcer FX] → WebRTC sender
  //
  // Either stage may be skipped; missing stages just pass-through.
  const rebuildAudioChain = ({ autoFix, announcer }) => {
    const raw = streamRef.current;
    if (!raw) return;
    try { announcerFxRef.current?.dispose?.(); } catch {}
    announcerFxRef.current = null;
    try { autoFixRef.current?.dispose?.(); } catch {}
    autoFixRef.current = null;

    let upstream = raw;
    if (autoFix) {
      try {
        autoFixRef.current = applyAutoFix(raw);
        upstream = autoFixRef.current.stream;
      } catch {
        toast.error("Couldn't enable Auto-Fix in this browser.");
      }
    }
    if (announcer) {
      try {
        announcerFxRef.current = applyAnnouncerFx(upstream);
        upstream = announcerFxRef.current.stream;
      } catch {
        toast.error("Couldn't enable Announcer FX in this browser.");
      }
    }

    const newAudio = upstream.getAudioTracks?.()[0];
    if (!newAudio) return;
    Object.values(pcsRef.current || {}).forEach((rec) => {
      try {
        const sender = rec.pc.getSenders().find((s) => s.track && s.track.kind === "audio");
        if (sender) sender.replaceTrack(newAudio);
      } catch {}
    });
  };

  // Toggle Announcer FX. Delegates to rebuildAudioChain so the AutoFix +
  // Announcer FX pair always stay coherent.
  const toggleAnnouncerFx = () => {
    const next = !announcerFxOn;
    setAnnouncerFxOn(next);
    rebuildAudioChain({ autoFix: autoFixOn, announcer: next });
  };

  // Toggle Auto-Fix from the AudioQualityMeter expanded panel.
  const toggleAutoFix = () => {
    const next = !autoFixOn;
    setAutoFixOn(next);
    rebuildAudioChain({ autoFix: next, announcer: announcerFxOn });
  };

  // --- Camera compositor (rotation / mirror / filter) — only runs when a
  // non-default camera setting is applied AND the camera stream is acquired.
  const compositedTrackRef = useRef(null);
  const camFxActive = !!(
    cameraSettings && (
      (cameraSettings.rotationDeg || 0) !== 0 ||
      cameraSettings.mirrorH ||
      (cameraSettings.brightness ?? 1) !== 1 ||
      (cameraSettings.contrast ?? 1) !== 1 ||
      (cameraSettings.saturation ?? 1) !== 1
    )
  );
  useCameraCompositor({
    active: camFxActive && !!media.stream,
    settings: cameraSettings,
    getSourceStream: () => streamRef.current,
    onCompositedTrack: (track) => {
      compositedTrackRef.current = track;
      // Swap track on every live PC
      Object.values(pcsRef.current).forEach(({ pc }) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (sender) {
          if (track) sender.replaceTrack(track).catch(() => {});
          else {
            const raw = streamRef.current?.getVideoTracks?.()[0];
            if (raw) sender.replaceTrack(raw).catch(() => {});
          }
        }
      });
    },
  });

  // --- Green-screen mode (admin / premium-only) ---
  // iter-182 rewrite: all GS logic now lives inside the self-contained
  // `GreenScreenStudio` component mounted in the preview overlay. It
  // owns its own compositor + UI + persistence and simply hands us a
  // produced track via `onCompositedTrack`. We do one job here: swap
  // that track onto the outbound senders + preview. When the child
  // passes `null`, we restore the raw camera.
  const canUseGreenScreen = !!user && ["admin", "premium_creator"].includes(user.role);
  const [gsTrack, setGsTrack] = useState(null);
  // Camera zoom — applied via MediaStreamTrack constraints when the
  // camera driver exposes a `zoom` capability (most Android phones,
  // iPhone Pro rear cams). Falls back to a CSS transform scale so
  // desktop webcams / iOS front cameras still get a usable zoom UX.
  const [zoom, setZoom] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [showZoomPanel, setShowZoomPanel] = useState(false);
  // Keep a ref mirror of gsTrack so the proxy's getPreviewStream()
  // always returns a freshly-composed preview (raw camera + GS track
  // if active). Mobile sticky preview polls this every 600ms.
  const gsTrackRef = useRef(null);
  useEffect(() => { gsTrackRef.current = gsTrack; }, [gsTrack]);

  // Detect zoom-constraint support on the current camera video track.
  // Chrome on Android + Safari on iPhone rear cams expose
  // `{ zoom: { min, max, step } }` in track.getCapabilities().
  useEffect(() => {
    const t = media.stream?.getVideoTracks?.()[0];
    if (!t || !t.getCapabilities) { setZoomSupported(false); return; }
    try {
      const caps = t.getCapabilities();
      setZoomSupported(!!caps.zoom);
    } catch { setZoomSupported(false); }
  }, [media.stream]);

  // Push zoom value to the underlying camera track when supported;
  // otherwise the CSS transform scale below applies to the <video>.
  useEffect(() => {
    const t = media.stream?.getVideoTracks?.()[0];
    if (!t || !zoomSupported) return;
    try {
      const caps = t.getCapabilities();
      const clamped = Math.max(caps.zoom.min || 1, Math.min(caps.zoom.max || 3, zoom));
      t.applyConstraints({ advanced: [{ zoom: clamped }] }).catch(() => {});
    } catch { /* ignore */ }
  }, [zoom, zoomSupported, media.stream]);

  // Whenever the composited track identity changes, swap it onto every
  // outbound PeerConnection + refresh the local preview. When it goes
  // back to `null` (GS disabled), restore the raw camera.
  useEffect(() => {
    const raw = media.stream?.getVideoTracks?.()[0];
    const next = gsTrack || raw;
    if (!next) return;
    Object.values(pcsRef.current).forEach(({ pc }) => {
      const sender = pc.getSenders().find((s) => s.track?.kind === "video");
      if (sender) sender.replaceTrack(next).catch(() => {});
    });
    try {
      const v = videoRef.current;
      if (v) {
        const audioTracks = media.stream?.getAudioTracks?.() || [];
        v.srcObject = new MediaStream([next, ...audioTracks]);
        v.muted = true;
        v.playsInline = true;
        v.play().catch(() => {});
      }
    } catch { /* best-effort preview sync */ }
  }, [gsTrack, media.stream]);

  useEffect(() => {
    return () => cleanup();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);
  useEffect(() => { onStatusChange?.(status); }, [status, onStatusChange]);

  // When a fresh media stream arrives, bind it to the preview video element
  // — but ONLY when the green-screen hook isn't actively providing a
  // composited track. The GS-track effect below owns srcObject while
  // `gsTrack` is live; clobbering it here would erase the composite.
  useEffect(() => {
    if (media.stream && videoRef.current && !gsTrack) {
      videoRef.current.srcObject = media.stream;
      videoRef.current.muted = true;
    }
    streamRef.current = media.stream;
    // If we're already live and media re-acquired (e.g. camera flip), renegotiate tracks on every PC
    if (status === "live" && media.stream) {
      Object.entries(pcsRef.current).forEach(async ([vid, rec]) => {
        const senders = rec.pc.getSenders();
        media.stream.getTracks().forEach((track) => {
          const sender = senders.find((s) => s.track?.kind === track.kind);
          // While GS is active, prefer the composited video over the
          // raw camera — otherwise a camera flip briefly drops the
          // background until the hook catches up one render later.
          const effective = track.kind === "video" && gsTrack ? gsTrack : track;
          if (sender) {
            sender.replaceTrack(effective).catch(() => {});
          } else {
            rec.pc.addTrack(effective, media.stream);
          }
        });
      });
    }
  }, [media.stream, status, gsTrack]);

  const cleanup = () => {
    if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    Object.values(pcsRef.current).forEach(({ pc, retryTimer }) => {
      try { pc.close(); } catch {}
      if (retryTimer) clearTimeout(retryTimer);
    });
    pcsRef.current = {};
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    media.stop();
    streamRef.current = null;
  };

  const startBroadcast = async () => {
    // Hard-block start when we have no auth — happens if the token expired
    // mid-session. Without this guard, the WS would silently 401 and the
    // user would just see "I'm ready" do nothing. Loud-fail instead.
    if (!streamId) {
      toast.error("Stream isn't ready yet — refresh and try again");
      return;
    }
    if (!token) {
      toast.error("You've been signed out — log in again to go live");
      setStatus("error");
      setErrMsg("Auth token missing");
      return;
    }
    // If we're already live (or in the middle of connecting), don't tear it
    // down — this prevents the Director's-Mode tab-switch from re-acquiring
    // the camera and re-opening the preflight on every audio/overlay tab.
    if (status === "live" || status === "requesting" || status === "reconnecting") {
      return;
    }
    const stream = await media.acquire();
    if (!stream) return; // permission gate will show
    let iceServers;
    try {
      iceServers = await fetchIceServers();
    } catch {
      iceServers = [{ urls: ["stun:stun.l.google.com:19302"] }];
    }
    if (!iceServers || !iceServers.length) {
      iceServers = [{ urls: ["stun:stun.l.google.com:19302"] }];
    }
    // Re-enable any track the user paused in the preview before going live —
    // a "Go Live" click should always result in an audible, visible feed.
    try {
      const vt = stream.getVideoTracks?.()[0];
      const at = stream.getAudioTracks?.()[0];
      if (vt && vt.enabled === false) vt.enabled = true;
      if (at && at.enabled === false) at.enabled = true;
    } catch {}
    setStatus("requesting");
    setErrMsg("Connecting to broadcast server…");
    connectSignaling(iceServers);
    iceServersRef.current = iceServers;
  };

  const connectSignaling = (iceServers, isReconnect = false) => {
    const url = makeWsUrl(`/api/ws/stream/${streamId}?role=host&token=${encodeURIComponent(token || "")}`);
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      setStatus("error");
      setErrMsg("Couldn't open broadcast channel — check your network");
      toast.error("Couldn't connect to broadcast server");
      return;
    }
    wsRef.current = ws;

    // Open-timeout watchdog — if the WS never reaches readyState 1 within 10s
    // the user has been staring at the green room close → black screen with
    // no feedback. Surface a real error and let them retry.
    const openTimeoutId = setTimeout(() => {
      if (ws.readyState !== 1) {
        try { ws.close(); } catch {}
        setStatus("error");
        setErrMsg("Broadcast server didn't respond — try again");
        toast.error("Broadcast server timeout. Tap Go Live to retry.");
      }
    }, 10000);

    ws.onopen = () => {
      clearTimeout(openTimeoutId);
      setStatus("live");
      setErrMsg("");
      reconnectAttemptsRef.current = 0;
      setReconnectAttempts(0);
      // Start keepalive pings — keeps WS alive through any proxy idle timeout
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      keepAliveRef.current = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping", payload: { t: Date.now() } }));
      }, 25000);
      // Host heartbeat — backend auto-finalizes the stream if we go silent for 45s
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: "host_heartbeat", payload: {} }));
      }, 10000);
      // If this was a reconnect, only restart ICE for viewers whose PC
      // is actually unhealthy. Healthy PCs continue to flow media
      // regardless of the signaling WS state — tearing them down on
      // every WS blip was THE root cause of viewers seeing "stream
      // cuts out every ~10s" and having to refresh. Keep the handshake
      // quiet and let WebRTC's own ICE keepalives do their job.
      if (isReconnect) {
        Object.entries(pcsRef.current).forEach(([viewerId, rec]) => {
          const ice = rec.pc.iceConnectionState;
          if (ice === "connected" || ice === "completed" || ice === "checking" || ice === "new") return;
          try { rec.pc.restartIce(); } catch {}
          sendOffer(viewerId, true);
        });
      }
    };
    ws.onclose = (e) => {
      clearTimeout(openTimeoutId);
      if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; }
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      // Diagnostic: log code + reason so we can actually see WHY the WS
      // closed in the field. Users reported "cuts out every 10s" — we
      // need this trail to pinpoint which close code is driving it.
      try { console.warn("[broadcaster] WS closed", { code: e?.code, reason: e?.reason, wasClean: e?.wasClean }); } catch {}
      // Intentional close: status = "ended" → don't reconnect
      if (status === "ended" || wantCloseRef.current) return;
      // Authentication failure (1008 from server) means the host token is
      // invalid or stream ownership doesn't match — reconnecting will
      // never succeed. Bail out loudly so the user can fix it.
      if (e?.code === 1008) {
        const reason = e?.reason || "";
        const looksLikeAuth = /login expired|sign in/i.test(reason);
        const looksLikeNotFound = /stream not found/i.test(reason);
        setStatus("error");
        if (looksLikeNotFound) {
          setErrMsg("This stream no longer exists. Tap End and start a fresh broadcast.");
          toast.error("Stream not found — start a new broadcast.");
        } else if (looksLikeAuth) {
          setErrMsg(reason);
          toast.error("Login expired — refresh & sign back in.");
        } else {
          setErrMsg(reason || "Server rejected the broadcast — refresh and sign in again");
          toast.error(reason || "Broadcast rejected by server. Refresh & sign in.");
        }
        return;
      }
      // Auto-reconnect with exponential backoff (max 10s)
      const attempts = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempts;
      setReconnectAttempts(attempts);
      if (attempts >= 6) {
        setStatus("error");
        setErrMsg("Broadcast server unreachable — check your network");
        toast.error("Couldn't reconnect after 6 tries. Tap Go Live to retry.");
        return;
      }
      const delay = Math.min(10000, 1000 * Math.pow(2, Math.min(attempts - 1, 4)));
      setStatus("reconnecting");
      setErrMsg(`Reconnecting in ${Math.round(delay / 1000)}s…`);
      reconnectTimerRef.current = setTimeout(() => connectSignaling(iceServers, true), delay);
    };
    ws.onerror = () => { /* onclose fires right after; handle there */ };

    ws.onmessage = async (evt) => {
      const msg = safeJson(evt.data);
      if (!msg) return;
      if (msg.type === "pong") return; // keepalive reply, no-op
      if (msg.type === "error") { setStatus("error"); setErrMsg(msg.payload?.detail || "Error"); return; }
      if (msg.type === "viewer_joined") {
        // A new viewer connected; wait for their viewer_ready to offer
        const v = msg.payload;
        setViewers((prev) => [...prev.filter((p) => p.user_id !== v.viewer_id), { user_id: v.viewer_id, name: v.name, avatar: v.avatar }]);
      } else if (msg.type === "viewer_ready") {
        await handleViewerReady(msg.payload.viewer_id, iceServers);
      } else if (msg.type === "answer") {
        const rec = pcsRef.current[msg.from];
        if (rec && msg.payload) {
          // Guard against "Failed to set remote answer: called in
          // wrong state". This fires whenever the PC isn't actually
          // waiting for an answer — i.e. it's in `stable` (already
          // completed negotiation) or `have-remote-offer` (wrong
          // direction). Happens when we spam offer-retries under a
          // flapping network and answers arrive out of order.
          const st = rec.pc.signalingState;
          if (st !== "have-local-offer") {
            // Swallow silently. Next offer-retry tick will produce a
            // fresh offer and a matching answer.
            return;
          }
          try {
            await rec.pc.setRemoteDescription(new RTCSessionDescription(msg.payload));
          } catch {
            // Host may have moved on between the check and apply —
            // safe to ignore; retry loop will heal.
          }
        }
      } else if (msg.type === "ice") {
        // ICE candidates may target either:
        //  (a) a viewer PC (host→viewer broadcast leg, pcsRef), OR
        //  (b) a guest publish PC (guest→host receive leg, guestPcsRef)
        // depending on which peer the `from` is. Check both maps so we
        // never silently drop candidates and stall a handshake.
        const recView = pcsRef.current[msg.from];
        const recGuest = guestPcsRef.current[msg.from];
        if (msg.payload) {
          if (recView) { try { await recView.pc.addIceCandidate(msg.payload); } catch {} }
          if (recGuest) { try { await recGuest.pc.addIceCandidate(msg.payload); } catch {} }
        }
      } else if (msg.type === "viewer_left") {
        const vid = msg.payload?.viewer_id;
        if (vid && pcsRef.current[vid]) {
          try { pcsRef.current[vid].pc.close(); } catch {}
          if (pcsRef.current[vid].retryTimer) clearTimeout(pcsRef.current[vid].retryTimer);
          delete pcsRef.current[vid];
          setViewers((prev) => prev.filter((v) => v.user_id !== vid));
          setViewerStates((s) => { const n = { ...s }; delete n[vid]; return n; });
        }
        // Also tear down any guest-publish PC for this peer so the PIP
        // slot empties when a guest disconnects mid-broadcast.
        if (vid && guestPcsRef.current[vid]) {
          try { guestPcsRef.current[vid].pc.close(); } catch {}
          delete guestPcsRef.current[vid];
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("tapin:guest-track-end", { detail: { guestId: vid } }));
          }
        }
      } else if (msg.type === "chat") {
        setChat((prev) => [...prev, msg.payload]);
      } else if (msg.type === "emote") {
        addEmote(msg.payload?.emote || "🎉");
      } else if (msg.type === "guest_publish_request") {
        // A guest is asking to publish their camera into the broadcast.
        // Auto-accept (the host is the one who invited them — they're
        // already trusted), then sit back and wait for the incoming
        // offer. The guest UI handles the offer side once it gets our
        // `guest_publish_accept` ack.
        const guestId = msg.payload?.guest_id || msg.from;
        if (guestId) {
          try {
            ws.send(JSON.stringify({
              type: "guest_publish_accept",
              to: guestId,
              payload: { name: msg.payload?.name, avatar: msg.payload?.avatar },
            }));
          } catch { /* ws likely closed */ }
        }
      } else if (msg.type === "offer" && msg.from) {
        // Incoming offer from a guest who wants to publish their camera
        // up to us (host). The standard flow has the HOST sending offers
        // TO viewers — incoming offers only arrive from guests who got
        // a `guest_publish_accept` ack. Create a RECV-ONLY peer
        // connection, set remote desc, answer back. When the guest's
        // track arrives, we dispatch a `tapin:guest-track` window event
        // so AdvancedDirectorPanel can route it into a PIP / split-
        // screen slot.
        await handleGuestOffer(msg.from, msg.payload, ws);
      }
    };
  };

  // ----------------------------------------------------------------
  // Guest publish — host-side receiver
  // ----------------------------------------------------------------
  const handleGuestOffer = async (guestId, sdp, ws) => {
    if (!sdp) return;
    try {
      let rec = guestPcsRef.current[guestId];
      if (!rec) {
        const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
        const stream = new MediaStream();
        rec = { pc, stream };
        guestPcsRef.current[guestId] = rec;
        pc.onicecandidate = (e) => {
          if (e.candidate) {
            try {
              ws.send(JSON.stringify({ type: "ice", to: guestId, payload: e.candidate.toJSON() }));
            } catch { /* ignore */ }
          }
        };
        pc.ontrack = (e) => {
          e.streams[0]?.getTracks().forEach((t) => stream.addTrack(t));
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("tapin:guest-track", {
              detail: { guestId, stream },
            }));
          }
        };
        pc.oniceconnectionstatechange = () => {
          if (["failed", "closed", "disconnected"].includes(pc.iceConnectionState)) {
            try { pc.close(); } catch { /* ignore */ }
            delete guestPcsRef.current[guestId];
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("tapin:guest-track-end", { detail: { guestId } }));
            }
          }
        };
      }
      await rec.pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const ans = await rec.pc.createAnswer();
      await rec.pc.setLocalDescription(ans);
      try {
        ws.send(JSON.stringify({ type: "answer", to: guestId, payload: { type: ans.type, sdp: ans.sdp } }));
      } catch { /* ignore */ }
    } catch (e) {
      console.warn("Guest offer handling failed:", e);
    }
  };

  const handleViewerReady = async (viewerId, iceServers) => {
    // Idempotent: if this viewer already has a healthy PC, keep it. Only
    // repeated viewer_ready frames with a clearly-broken ICE state trigger
    // a fresh PC. (Viewers occasionally spam viewer_ready — keep the
    // renegotiation cost low so they don't flicker.)
    const existing = pcsRef.current[viewerId];
    if (existing?.pc) {
      const st = existing.pc.iceConnectionState;
      if (st === "connected" || st === "completed" || st === "checking") {
        // Healthy PC already exists — viewer is just re-announcing
        // readiness (their retry loop). Do NOT nudge with a fresh
        // offer: it drives the PC from `stable` into `have-local-offer`,
        // and if the viewer is mid-handshake on the previous cycle
        // the answers arrive out of order and we hit "Failed to set
        // remote answer: called in wrong state". Staying silent is
        // correct — the viewer is already receiving the stream.
        return;
      }
      // Otherwise fall through to rebuild (failed / closed / disconnected).
      try { existing.pc.close(); } catch {}
      if (existing.retryTimer) clearTimeout(existing.retryTimer);
    }
    const pc = newPeerConnection(iceServers);
    pcsRef.current[viewerId] = { pc, retryTimer: null };

    streamRef.current?.getTracks().forEach((t) => {
      // Use the effective audio track (post Auto-Fix / Announcer FX) so new
      // viewers join already hearing the processed feed without a glitch.
      if (t.kind === "audio") {
        const effective = getEffectiveAudioTrack();
        if (effective) { pc.addTrack(effective, streamRef.current); return; }
      }
      // For video, if Green Screen is active, send the COMPOSITED
      // track (camera keyed + background). Without this, every viewer
      // joining after GS was enabled sees raw green wall until the
      // next time the composite track changes identity. This was the
      // "viewers see raw green screen" bug.
      if (t.kind === "video") {
        const gs = gsTrackRef.current;
        if (gs) { pc.addTrack(gs, streamRef.current); return; }
      }
      pc.addTrack(t, streamRef.current);
    });

    // Raise the outbound video bitrate. Without this most browsers cap
    // unidirectional WebRTC video around ~500 kbps, which makes 1080p
    // input look mushy on viewers' screens. 4 Mbps matches the bitrate
    // autopilot's top tier (it downsteps automatically under real
    // congestion). degradationPreference=maintain-resolution makes the
    // encoder drop FRAMERATE instead of resolution when squeezed — for
    // a talking-head/composited feed, sharpness beats fps.
    try {
      pc.getSenders().forEach((sender) => {
        if (sender.track?.kind !== "video") return;
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        params.degradationPreference = "maintain-resolution";
        let prefFps = 30;
        try { prefFps = JSON.parse(localStorage.getItem("tv_stream_quality") || "{}")?.fps || 30; } catch { /* default */ }
        params.encodings.forEach((enc) => {
          enc.maxBitrate = 4_000_000;
          enc.maxFramerate = prefFps;
          enc.priority = "high";
          enc.networkPriority = "high";
        });
        sender.setParameters(params).catch(() => {});
      });
    } catch { /* setParameters not supported on this browser — fine */ }

    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "ice", to: viewerId, payload: e.candidate.toJSON() });
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      setViewerStates((s) => ({ ...s, [viewerId]: st }));
      if (st === "failed") {
        // Full ICE restart — fresh candidates, fresh offer
        try { pc.restartIce(); } catch {}
        sendOffer(viewerId, true);
      } else if (st === "disconnected") {
        // Give it a beat to self-heal (mobile network blips), then retry
        scheduleOfferRetry(viewerId, iceServers);
      }
    };

    await sendOffer(viewerId);
    // 5s retry loop until connected
    const tick = async () => {
      const rec = pcsRef.current[viewerId];
      if (!rec) return;
      const ice = rec.pc.iceConnectionState;
      if (ice === "connected" || ice === "completed") return;
      // Don't send a new offer while the previous one hasn't been
      // answered yet — that causes out-of-order answers that fail with
      // "called in wrong state".
      if (rec.pc.signalingState !== "have-local-offer") {
        await sendOffer(viewerId);
      }
      rec.retryTimer = setTimeout(tick, 5000);
    };
    pcsRef.current[viewerId].retryTimer = setTimeout(tick, 5000);
  };

  const scheduleOfferRetry = (viewerId, iceServers) => {
    const rec = pcsRef.current[viewerId];
    if (!rec) return;
    if (rec.retryTimer) clearTimeout(rec.retryTimer);
    rec.retryTimer = setTimeout(async () => { await sendOffer(viewerId); }, 2000);
  };

  const sendOffer = async (viewerId, iceRestart = false) => {
    const rec = pcsRef.current[viewerId];
    if (!rec) return;
    try {
      const offer = await rec.pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
      await rec.pc.setLocalDescription(offer);
      send({ type: "offer", to: viewerId, payload: { type: offer.type, sdp: offer.sdp } });
    } catch (e) { /* ignore */ }
  };

  const send = (frame) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(frame));
  };

  const toggleCam = () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    track.enabled = !track.enabled; setCamOn(track.enabled);
  };
  const toggleMic = () => {
    const track = streamRef.current?.getAudioTracks?.()[0];
    if (!track) return;
    track.enabled = !track.enabled; setMicOn(track.enabled);
  };

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatMsg.trim()) return;
    send({ type: "chat", payload: { text: chatMsg.trim() } });
    setChatMsg("");
  };

  // ---- Recording (browser MediaRecorder of local preview) ----
  const pickMime = () => {
    const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
    for (const c of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  };

  const startRecording = () => {
    if (!streamRef.current) { toast.error("Camera not ready"); return; }
    if (typeof MediaRecorder === "undefined") { toast.error("Recording not supported in this browser"); return; }
    try {
      chunksRef.current = [];
      const mime = pickMime();
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });
        chunksRef.current = [];
        if (!blob.size) return;
        await uploadReplay(blob, mime || "video/webm");
      };
      rec.start(5000); // 5s chunks
      recorderRef.current = rec;
      setRecording(true);
      toast.success("Recording started");
    } catch (e) {
      toast.error("Could not start recording: " + (e?.message || e));
    }
  };

  const stopRecordingInternal = () => {
    try {
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
    } catch {}
    recorderRef.current = null;
    setRecording(false);
  };

  const uploadReplay = async (blob, mime) => {
    try {
      setUploadState("uploading");
      const ext = (mime.split("/")[1] || "webm").split(";")[0];
      const form = new FormData();
      form.append("file", new File([blob], `replay-${streamId}.${ext}`, { type: mime }));
      form.append("publish", "false");
      const { data } = await api.post(`/live/${streamId}/recording-upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setReplayVideoId(data.video_id);
      setUploadState("saved");
      toast.success("Replay saved to your library");
    } catch (e) {
      setUploadState("failed");
      toast.error("Replay upload failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    }
  };

  const endBroadcast = () => {
    wantCloseRef.current = true;
    send({ type: "end_broadcast" });
    if (recording) stopRecordingInternal();
    setStatus("ended");
    // Delay cleanup slightly so MediaRecorder.onstop can flush the last chunk
    setTimeout(() => { cleanup(); }, 1500);
    onEnd?.();
  };

  // ---- Imperative API for Director-mode wrappers (music mixer, overlays, multi-cam) ----
  useImperativeHandle(ref, () => ({
    get status() { return status; },
    get media() { return media; },
    get streamRef() { return streamRef; },
    get pcs() { return pcsRef.current; },
    /** Toggle the broadcaster microphone (mute/unmute). Surfaces the
     *  same `toggleMic` the inline broadcaster controls already drive,
     *  so external surfaces like MobileLiveStudio's right-side icon
     *  column stay in sync with the host's intent. */
    toggleMic: () => toggleMic(),
    getMicOn: () => micOn,
    /** Return the current outbound MediaStream — used by MobileLiveStudio
     *  to mirror the host preview + hand the raw stream to the mobile
     *  Green Screen compositor. The stream reference changes on camera
     *  flip so the compositor picks up the new device automatically. */
    getMediaStream: () => media.stream || streamRef.current || null,
    // Surface the green-screen capability flag so wrappers (like
    // AdvancedDirectorPanel) can avoid running their OWN legacy GS
    // compositor on top of this one — double-compositors race on the
    // outbound `replaceTrack` call and the host's background picks
    // never "win". iter-180 rebuild.
    get canUseGreenScreen() { return canUseGreenScreen; },
    /** Returns the currently-composited video track, or null when GS
     *  isn't active. Used by the mobile sticky preview bar to show the
     *  same GS-applied frames the viewers are seeing. */
    getCompositedTrack() { return gsTrackRef.current; },
    /** Drive the green-screen composited track from an external owner
     *  (the new mobile-first MobileGreenScreenBar). When set to a
     *  track, all outbound senders + host preview swap to it via the
     *  same gsTrack effect the embedded GreenScreenStudio uses; when
     *  null, raw camera is restored. */
    setCompositedTrack(track) { setGsTrack(track || null); },
    /** Returns an effective preview MediaStream: GS-composited video
     *  (if active) + current camera audio, otherwise the raw camera
     *  stream. External surfaces (MobileLivePreviewBar) call this
     *  instead of `streamRef.current` so they mirror exactly what the
     *  outbound viewers see. Returns a new MediaStream on every call
     *  so pollers' identity check fires when GS toggles on/off. */
    getPreviewStream() {
      const raw = streamRef.current;
      if (!raw) return null;
      const gs = gsTrackRef.current;
      if (!gs) return raw;
      try {
        return new MediaStream([gs, ...(raw.getAudioTracks?.() || [])]);
      } catch {
        return raw;
      }
    },
    /** Live outbound stats snapshot for the Stream Health HUD — pulls
     *  outbound-rtp video from the first connected peer, or falls back
     *  to the local track's settings when broadcasting with 0 viewers. */
    async getOutboundStats() {
      const entries = Object.values(pcsRef.current);
      for (const { pc } of entries) {
        if (!pc || pc.connectionState === "closed") continue;
        try {
          const stats = await pc.getStats();
          let out = null;
          stats.forEach((r) => { if (r.type === "outbound-rtp" && r.kind === "video") out = r; });
          if (out) {
            return {
              viewers: entries.length,
              bytesSent: out.bytesSent || 0,
              fps: out.framesPerSecond || 0,
              width: out.frameWidth || 0,
              height: out.frameHeight || 0,
              timestamp: out.timestamp || Date.now(),
              qualityLimitation: out.qualityLimitationReason || "none",
            };
          }
        } catch { /* peer closed mid-read — try next */ }
      }
      const t = gsTrackRef.current || streamRef.current?.getVideoTracks?.()[0] || null;
      const s = t?.getSettings?.() || {};
      return {
        viewers: 0,
        bytesSent: 0,
        fps: s.frameRate || 0,
        width: s.width || 0,
        height: s.height || 0,
        timestamp: Date.now(),
        qualityLimitation: "none",
      };
    },
    /** Replace the outgoing audio track on every live PC with the given track. */
    replaceAudioTrack(track) {
      Object.values(pcsRef.current).forEach(({ pc }) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === "audio");
        if (sender && track) sender.replaceTrack(track).catch(() => {});
      });
    },
    /** Replace the outgoing video track on every live PC with the given track. */
    replaceVideoTrack(track) {
      Object.values(pcsRef.current).forEach(({ pc }) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (sender && track) sender.replaceTrack(track).catch(() => {});
      });
      // Also reflect in host preview
      const v = videoRef.current;
      if (v && track) {
        const ms = new MediaStream([track, ...(streamRef.current?.getAudioTracks() || [])]);
        try { v.srcObject = ms; } catch {}
      }
      // Stash the most-recent outbound video track so getRecordableStream
      // can pull it even when no peer is currently connected (e.g. a host
      // testing locally with zero viewers). Without this, highlight clips
      // captured before the first viewer connect would have no video.
      lastOutboundVideoTrackRef.current = track || null;
    },
    /** Build a MediaStream that mirrors exactly what the viewers are
     *  receiving right now — first audio sender + first video sender
     *  across all active peer connections. Used by the highlight
     *  recorder so clips capture composites (PIP / side-by-side /
     *  Guest-Take-Full / Green-Screen), not just the raw camera. */
    getRecordableStream() {
      const tracks = [];
      // Pull from the first PC that has both senders attached.
      for (const { pc } of Object.values(pcsRef.current)) {
        const aSender = pc.getSenders().find((s) => s.track?.kind === "audio");
        const vSender = pc.getSenders().find((s) => s.track?.kind === "video");
        if (aSender?.track) { tracks.push(aSender.track); }
        if (vSender?.track) { tracks.push(vSender.track); }
        if (tracks.length) break;
      }
      // Fall back: no peers yet → use the stashed last-replaced video
      // track (so side-by-side / Take-Full mid-broadcast still record)
      // and the raw camera audio.
      if (!tracks.length) {
        const v = lastOutboundVideoTrackRef.current || streamRef.current?.getVideoTracks?.()[0];
        const a = streamRef.current?.getAudioTracks?.()[0];
        if (v) tracks.push(v);
        if (a) tracks.push(a);
      }
      try { return new MediaStream(tracks); } catch { return null; }
    },
    /** Send newsroom overlays to viewers via WS. */
    sendOverlays(overlays) {
      send({ type: "overlays", payload: overlays || {} });
    },
    /** Toggle viewer comments on/off — host-only WS frame. */
    sendCommentsToggle(enabled) {
      send({ type: "comments_toggle", payload: { enabled: !!enabled } });
    },
    /** Flip the device camera (front <-> back on mobile). */
    flipCamera: () => media.flipCamera?.(),
    /** Re-acquire the camera with new resolution / fps constraints. */
    reacquireCamera: async ({ width, height, fps, aspectRatio } = {}) => {
      const videoConstraints = {
        width: width ? { ideal: width } : undefined,
        height: height ? { ideal: height } : undefined,
        frameRate: fps ? { ideal: fps, max: fps } : undefined,
        // Override the base 16:9 default when the caller is explicitly
        // asking for a portrait acquisition (e.g. MobileLiveStudio
        // requesting 720×1280). Without this, the browser splits the
        // difference between width/height ideals and the 16:9 aspect
        // hint and often returns landscape on phones.
        aspectRatio: aspectRatio
          ? { ideal: aspectRatio }
          : (width && height ? { ideal: width / height } : undefined),
      };
      const stream = await media.acquire({ force: true, videoConstraints });
      // Swap outbound video track
      const track = stream?.getVideoTracks?.()[0];
      if (track) {
        Object.values(pcsRef.current).forEach(({ pc }) => {
          const sender = pc.getSenders().find((s) => s.track?.kind === "video");
          if (sender) sender.replaceTrack(track).catch(() => {});
        });
      }
      return !!stream;
    },
    /** Send start/end broadcast programmatically from a wrapper panel. */
    startBroadcast, endBroadcast,
    /** Start / stop local recording (toolbar wrapper in DirectorConsole). */
    startRecording, stopRecording: stopRecordingInternal,
    get isRecording() { return recording; },
  }), [status, media, recording, canUseGreenScreen]);

  // ---- render ----
  return (
    <div data-testid="live-broadcaster" className="tv-glass rounded-2xl border border-white/10 overflow-hidden">
      <div className={hideChat ? "" : "grid md:grid-cols-[1fr_320px]"}>
        <div className="relative bg-black aspect-video" ref={previewContainerRef}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover transition-[transform,filter] duration-200"
            style={{
              ...(camFxActive ? cameraSettingsToCss(cameraSettings) : {}),
              // CSS zoom fallback — only engages when the camera track
              // doesn't natively support the `zoom` constraint, so we
              // don't double-scale on phones that already zoomed.
              transform: !zoomSupported && zoom !== 1 ? `scale(${zoom})` : undefined,
              transformOrigin: "center center",
            }}
          />
          <StreamWatermark variant={kidsBrand ? "kids" : "main"} size="sm" />
          <EmoteOverlay />
          {status === "live" && <ViewerStatsHud viewers={viewers} viewerStates={viewerStates} />}

          {/* Going-Live countdown reveal — big centred 3-2-1 pop the
              moment the stream lights up. Fades to nothing after 3 s. */}
          {liveCountdown > 0 && (
            <div
              data-testid="going-live-countdown"
              className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none bg-black/40"
            >
              <div
                key={liveCountdown}
                className="text-white font-display font-black text-[9rem] leading-none tracking-tighter drop-shadow-[0_6px_30px_rgba(0,0,0,0.85)]"
                style={{ animation: "gs-count-pop 900ms ease-out forwards" }}
              >
                {liveCountdown}
              </div>
            </div>
          )}

          {/* Camera zoom chip — top-left, always visible while on air so
              creators can dial-in tighter framing without hunting for
              settings. Uses native camera zoom when available, CSS
              fallback otherwise. */}
          {status === "live" && (
            <div className="absolute top-3 left-3 z-10 flex flex-col items-start gap-1.5">
              <button
                type="button"
                onClick={() => setShowZoomPanel((v) => !v)}
                data-testid="broadcaster-zoom-chip"
                title={zoomSupported ? "Camera zoom — native driver" : "Preview zoom — CSS fallback"}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition border ${
                  showZoomPanel
                    ? "bg-tv-accent text-black border-tv-accent"
                    : zoom !== 1
                      ? "bg-tv-accent/90 text-black border-tv-accent"
                      : "bg-black/60 border-white/15 text-white hover:text-tv-accent"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  <line x1="11" y1="8" x2="11" y2="14"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
                {zoom === 1 ? "Zoom" : `${zoom.toFixed(1)}x`}
              </button>
              {showZoomPanel && (
                <div
                  data-testid="broadcaster-zoom-panel"
                  className="bg-black/90 backdrop-blur-md border border-white/15 rounded-xl px-3 py-2 flex items-center gap-2"
                >
                  <span className="text-[9px] font-black text-white/50 uppercase tracking-wider">1x</span>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    data-testid="broadcaster-zoom-slider"
                    className="w-36 accent-tv-accent"
                    aria-label="Camera zoom"
                  />
                  <span className="text-[10px] font-black text-tv-accent tabular-nums w-10 text-right">{zoom.toFixed(1)}x</span>
                  {zoom !== 1 && (
                    <button
                      type="button"
                      onClick={() => setZoom(1)}
                      data-testid="broadcaster-zoom-reset"
                      className="text-[9px] font-black text-white/60 hover:text-white uppercase tracking-wider"
                    >
                      Reset
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Green-Screen Studio — desktop only. On mobile, the GS UX
              is owned by `MobileGreenScreenBar` inside MobileLiveStudio
              (it bubbles the composited track to us via the proxy's
              new `setCompositedTrack`). Hiding the inline studio on
              phones avoids double-compositor races and removes the
              overlapping panel the user reported. */}
          {canUseGreenScreen && (
            <div className="absolute top-3 right-3 z-10 hidden lg:block" data-testid="broadcaster-greenscreen-wrap">
              <GreenScreenStudio
                cameraStream={media.stream || null}
                onCompositedTrack={setGsTrack}
                testidPrefix="broadcaster-gs"
              />
            </div>
          )}

          {status !== "live" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/75">
              {status === "idle" && media.state !== "denied" && media.state !== "error" && !hideInlineControls && (
                <button
                  data-testid="broadcaster-start-btn"
                  onClick={startBroadcast}
                  className="bg-tv-live hover:bg-red-600 text-white font-bold px-6 py-4 rounded-full flex items-center gap-2"
                >
                  <Radio className="w-5 h-5" /> Start Camera + Go Live
                </button>
              )}
              {status === "idle" && media.state !== "denied" && media.state !== "error" && hideInlineControls && (
                <div className="text-white/70 text-xs text-center px-4" data-testid="broadcaster-idle-hint">
                  Use the <span className="text-tv-live font-black">Start</span> button in the top toolbar to go live.
                </div>
              )}
              {status === "ended" && <div className="text-white/80 font-semibold">Broadcast ended.</div>}
              {status === "reconnecting" && (
                <div className="flex flex-col items-center gap-3 text-white" data-testid="broadcaster-reconnecting">
                  <div className="animate-spin w-10 h-10 border-2 border-tv-accent border-t-transparent rounded-full" />
                  <div className="font-display font-bold text-lg">Reconnecting…</div>
                  <div className="text-white/60 text-xs">{errMsg || "Re-establishing link to viewers"}{reconnectAttempts ? ` · attempt ${reconnectAttempts}` : ""}</div>
                </div>
              )}
              {status === "error" && (
                <div className="flex flex-col items-center gap-3 text-white" data-testid="broadcaster-error">
                  <div className="font-display font-bold text-lg text-red-400">Stream error</div>
                  <div className="text-white/60 text-xs">{errMsg || "Please try again"}</div>
                </div>
              )}
            </div>
          )}

          {/* Camera permission overlay — visible whenever camera isn't ready OR during device picker when live */}
          {(media.state === "requesting" || media.state === "denied" || media.state === "error") && (
            <CameraPermissionGate
              state={media.state}
              error={media.error}
              onAcquire={startBroadcast}
              devices={media.devices}
              current={media.current}
              onSelect={media.selectDevice}
              onFlip={media.flipCamera}
              primaryLabel="Go Live"
            />
          )}
          {media.state === "ready" && (
            <CameraPermissionGate
              state="ready"
              devices={media.devices}
              current={media.current}
              onSelect={media.selectDevice}
              onFlip={media.flipCamera}
            />
          )}

          {status === "live" && !hideCrewBoost && (
            <div className="absolute top-3 right-3">
              <CrewBoostButton streamId={streamId} compact />
            </div>
          )}

          {status === "live" && (
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className="bg-tv-live text-white text-[10px] font-bold uppercase px-2 py-1 rounded flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white tv-pulse" /> ON AIR
              </span>
              <span className="bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                <Users className="w-3 h-3" /> {viewers.length}
              </span>
            </div>
          )}

          {status === "live" && !hideInlineControls && (
            <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-2">
              <button data-testid="broadcaster-toggle-mic" onClick={toggleMic} className={`w-11 h-11 rounded-full flex items-center justify-center ${micOn ? "bg-white/15 text-white" : "bg-red-500/80 text-white"}`}>
                {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>
              <button data-testid="broadcaster-toggle-cam" onClick={toggleCam} className={`w-11 h-11 rounded-full flex items-center justify-center ${camOn ? "bg-white/15 text-white" : "bg-red-500/80 text-white"}`}>
                {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
              <button
                type="button"
                data-testid="broadcaster-flip-cam"
                onClick={() => media.flipCamera?.()}
                title="Flip / switch camera"
                className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
              >
                <SwitchCamera className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="broadcaster-announcer-fx"
                onClick={toggleAnnouncerFx}
                title={announcerFxOn ? "Announcer FX on — broadcast voice" : "Turn on Announcer FX"}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition ${
                  announcerFxOn
                    ? "bg-tv-accent text-black"
                    : "bg-white/15 hover:bg-white/25 text-white"
                }`}
              >
                <Sparkles className="w-5 h-5" />
              </button>
              {/* Mic level meter — small but always visible so hosts know
                  if they're clipping or dead-quiet without leaving the
                  broadcast. Reads the post-FX track when Announcer FX is
                  on (so the level reflects what viewers actually hear). */}
              <AudioLevelMeter
                stream={announcerFxOn && announcerFxRef.current
                  ? announcerFxRef.current.stream
                  : streamRef.current}
                on={micOn}
                className="self-center h-11"
              />
              {/* Audio quality diagnostics — collapsed by default. Surfaces
                  clipping, noise floor, SNR, mic dropout, and feedback so
                  hosts can fix audio issues before viewers complain. We
                  intentionally read the *raw* mic stream (not the post-FX
                  one) so creators see their actual room conditions. The
                  panel also exposes a one-tap Auto-Fix toggle that wires
                  a clip-limiter, noise gate, and tracking notch upstream
                  of Announcer FX. */}
              <div className="relative self-center">
                <AudioQualityMeter
                  stream={streamRef.current}
                  on={micOn}
                  autoFixOn={autoFixOn}
                  onToggleAutoFix={toggleAutoFix}
                  getAutoFixStatus={() => autoFixRef.current?.getStatus?.() || null}
                />
              </div>
              <button
                type="button"
                data-testid="broadcaster-fullscreen"
                onClick={() => {
                  const el = previewContainerRef.current;
                  if (!el) return;
                  if (!document.fullscreenElement) (el.requestFullscreen?.() || el.webkitRequestFullscreen?.())?.catch?.(() => {});
                  else (document.exitFullscreen?.() || document.webkitExitFullscreen?.())?.catch?.(() => {});
                }}
                title="Fullscreen preview"
                className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
              >
                <Maximize className="w-5 h-5" />
              </button>
              {recording ? (
                <button
                  data-testid="broadcaster-stop-record-btn"
                  onClick={stopRecordingInternal}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-4 rounded-full flex items-center gap-2"
                  title="Stop recording (replay uploads automatically)"
                >
                  <CircleDot className="w-4 h-4 animate-pulse" /> REC
                </button>
              ) : (
                <button
                  data-testid="broadcaster-start-record-btn"
                  onClick={startRecording}
                  className="bg-white/15 hover:bg-white/25 text-white font-semibold px-4 rounded-full flex items-center gap-2"
                  title="Record this broadcast as a replay"
                >
                  <CircleDot className="w-4 h-4" /> Record
                </button>
              )}
              <button data-testid="broadcaster-end-btn" onClick={endBroadcast} className="bg-tv-live hover:bg-red-600 text-white font-bold px-4 rounded-full flex items-center gap-2">
                <Square className="w-4 h-4 fill-current" /> End
              </button>
            </div>
          )}

          {status === "live" && recording && (
            <div className="absolute top-3 left-[160px] bg-red-600/90 text-white text-[10px] font-bold uppercase px-2 py-1 rounded flex items-center gap-1" data-testid="broadcaster-rec-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-white tv-pulse" /> REC
            </div>
          )}

          {status === "ended" && uploadState === "uploading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/85 z-20">
              <div className="text-center text-white" data-testid="broadcaster-upload-uploading">
                <div className="animate-spin w-8 h-8 border-2 border-tv-accent border-t-transparent rounded-full mx-auto mb-3" />
                <div className="font-display font-bold">Uploading replay…</div>
                <div className="text-white/60 text-xs mt-1">Do not close this tab</div>
              </div>
            </div>
          )}
          {status === "ended" && uploadState === "saved" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/85 z-20">
              <div className="text-center text-white" data-testid="broadcaster-upload-saved">
                <div className="font-display font-bold text-lg">Replay saved ✓</div>
                <div className="text-white/60 text-xs mt-1">Find it in Studio → Library</div>
                {replayVideoId && (
                  <a href={`/watch/${replayVideoId}`} className="inline-block mt-3 bg-tv-accent text-black font-bold px-4 py-2 rounded-full text-xs">Open replay</a>
                )}
              </div>
            </div>
          )}
          {status === "ended" && uploadState === "failed" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/85 z-20">
              <div className="text-center text-white" data-testid="broadcaster-upload-failed">
                <div className="font-display font-bold text-lg text-red-400">Replay upload failed</div>
                <div className="text-white/60 text-xs mt-1">The live session still ended OK.</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col max-h-[560px]" hidden={hideChat} style={hideChat ? { display: "none" } : undefined}>
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white font-display font-bold">
            <MessageSquare className="w-4 h-4" /> Live chat
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
            {chat.length === 0 && <div className="text-white/40 text-xs">No messages yet.</div>}
            {chat.map((m) => (
              <div key={m.id} className="flex gap-2 items-start" data-testid={`broadcaster-chat-${m.id}`}>
                <div className="w-6 h-6 rounded-full bg-tv-brand text-black font-bold flex items-center justify-center text-xs shrink-0">{m.name?.[0]}</div>
                <div>
                  <span className="text-tv-brand font-semibold text-xs">{m.name}</span>{" "}
                  <span className="text-white/85">{m.text}</span>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex gap-2">
            <input data-testid="broadcaster-chat-input" value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} placeholder="Message your viewers" className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-sm text-white outline-none" />
            <button className="bg-tv-accent text-black rounded-full px-3"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      </div>
    </div>
  );
});

function safeJson(s) { try { return JSON.parse(s); } catch { return null; } }

export default LiveBroadcaster;
